import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiPatch, formatDateTime, formatDate } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Save, Sparkles, Crown, Hourglass, ShieldCheck, Check, X,
  RotateCcw, AlertTriangle, Activity, Loader2, Mail, User as UserIcon, Globe2,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TIER_BADGE = {
  basic:   "bg-muted text-muted-foreground border-border",
  trial:   "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  premium: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
};
const STATUS_BADGE = {
  pending:  "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30",
  approved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  rejected: "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};

const Section = ({ icon: Icon, title, children, action }) => (
  <div className="rounded-lg border bg-card">
    <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
      <div className="flex items-center gap-2 text-sm font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5 text-primary" />}
        {title}
      </div>
      {action}
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const UserDetailModal = ({ open, user, onClose, onUserUpdated }) => {
  const [u, setU] = useState(user);
  const [busy, setBusy] = useState({});
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [tierDraft, setTierDraft] = useState("basic");
  const [extendDays, setExtendDays] = useState(7);
  const [trialExpiryInput, setTrialExpiryInput] = useState("");
  const [premiumExpiresInput, setPremiumExpiresInput] = useState("");
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(null); // 'reject' | 'suspend'
  const [reasonText, setReasonText] = useState("");

  useEffect(() => {
    if (!user) return;
    setU(user);
    setFirst(user.first_name || "");
    setLast(user.last_name || "");
    setPhone(user.phone || "");
    setTierDraft(user.tier || "basic");
    setPremiumExpiresInput(user.premium_expires_at ? user.premium_expires_at.slice(0, 10) : "");
    setTrialExpiryInput(user.trial_expires_at ? user.trial_expires_at.slice(0, 10) : "");
  }, [user]);

  useEffect(() => {
    if (!open || !user?.user_id) return;
    setActivityLoading(true);
    apiGet(`/admin/users/${user.user_id}/activity?limit=10`)
      .then(setActivity)
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [open, user?.user_id]);

  if (!user || !u) return null;

  const refresh = (updated) => {
    setU(updated);
    onUserUpdated?.(updated);
  };

  const setBusyKey = (k, v) => setBusy((b) => ({ ...b, [k]: v }));

  const saveProfile = async () => {
    if (!first.trim() || !last.trim()) {
      toast.error("First and last name are required");
      return;
    }
    setBusyKey("profile", true);
    try {
      const res = await apiPatch(`/admin/users/${u.user_id}/profile`, {
        first_name: first.trim(), last_name: last.trim(), phone: phone.trim(),
      });
      refresh(res);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save profile");
    } finally { setBusyKey("profile", false); }
  };

  const saveTier = async () => {
    if (!tierDraft || tierDraft === u.tier) return;
    setBusyKey("tier", true);
    try {
      const res = await apiPatch(`/admin/users/${u.user_id}/tier`, { tier: tierDraft });
      refresh(res);
      toast.success(`Tier changed to ${tierDraft}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change tier");
    } finally { setBusyKey("tier", false); }
  };

  const extend = async () => {
    const d = Number(extendDays);
    if (!d || d < 1) { toast.error("Enter a positive number of days"); return; }
    setBusyKey("extend", true);
    try {
      const res = await apiPost(`/admin/users/${u.user_id}/extend-trial`, { days: d });
      refresh(res);
      toast.success(`Trial extended by ${d} day${d === 1 ? "" : "s"} — now expires ${formatDate(res.trial_expires_at)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to extend trial");
    } finally { setBusyKey("extend", false); }
  };

  const setTrialDate = async () => {
    if (!trialExpiryInput) { toast.error("Pick a date first"); return; }
    setBusyKey("set-date", true);
    try {
      // Convert YYYY-MM-DD to a local end-of-day ISO datetime so the trial
      // expires at 23:59 in the admin's local timezone (not 23:59 UTC, which
      // would shift to the next day in non-UTC timezones).
      const [yy, mm, dd] = trialExpiryInput.split("-").map(Number);
      const localEndOfDay = new Date(yy, (mm || 1) - 1, dd || 1, 23, 59, 59, 0);
      const res = await apiPost(`/admin/users/${u.user_id}/set-trial-expiry`, {
        expiry_date: localEndOfDay.toISOString(),
      });
      refresh(res);
      toast.success(`Trial expiry set — now expires ${formatDate(res.trial_expires_at)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set trial expiry");
    } finally { setBusyKey("set-date", false); }
  };

  const grantPremium = async () => {
    setBusyKey("grant", true);
    try {
      let isoExpires = null;
      if (premiumExpiresInput) {
        const [yy, mm, dd] = premiumExpiresInput.split("-").map(Number);
        isoExpires = new Date(yy, (mm || 1) - 1, dd || 1, 23, 59, 59, 0).toISOString();
      }
      const res = await apiPost(`/admin/users/${u.user_id}/grant-premium`, {
        premium_expires_at: isoExpires,
      });
      refresh(res);
      toast.success(premiumExpiresInput ? `Premium granted until ${formatDate(res.premium_expires_at)}` : "Premium granted (indefinite)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant premium");
    } finally { setBusyKey("grant", false); }
  };

  const approve = async () => {
    setBusyKey("approve", true);
    try {
      const res = await apiPost(`/admin/users/${u.user_id}/approve`);
      refresh(res);
      toast.success("Account approved — 14-day Premium trial activated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusyKey("approve", false); }
  };

  const reactivate = async () => {
    setBusyKey("reactivate", true);
    try {
      const res = await apiPost(`/admin/users/${u.user_id}/reactivate`);
      refresh(res);
      toast.success("Account reactivated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reactivate failed");
    } finally { setBusyKey("reactivate", false); }
  };

  const submitReason = async () => {
    if (!reasonOpen) return;
    const isSuspension = reasonOpen === "suspend";
    if (isSuspension && !reasonText.trim()) { toast.error("Reason required to suspend"); return; }
    setBusyKey("reason", true);
    try {
      const res = await apiPost(`/admin/users/${u.user_id}/reject`, {
        is_suspension: isSuspension, reason: reasonText.trim(),
      });
      refresh(res);
      toast.success(isSuspension ? "Account suspended" : "Registration rejected");
      setReasonOpen(null);
      setReasonText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyKey("reason", false); }
  };

  // ---- Feature flag: US Stocks ------------------------------------------
  // Admin role always has access regardless of the flag (server-side too).
  // Flag persists on the user document via /api/admin/us-stocks/enable-flag.
  const isAdmin = u?.role === "admin";
  const usStocksEnabled = isAdmin || !!u?.us_stocks_enabled;
  const toggleUsStocks = async (next) => {
    setBusyKey("us_stocks", true);
    try {
      await apiPost(`/admin/us-stocks/enable-flag`, {
        user_id: u.user_id,
        enabled: !!next,
      });
      const updated = { ...u, us_stocks_enabled: !!next };
      setU(updated);
      onUserUpdated?.(updated);
      toast.success(next ? "US Stocks enabled for this user" : "US Stocks disabled for this user");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update access");
    } finally {
      setBusyKey("us_stocks", false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto"
          data-testid="user-detail-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" />
              {u.full_name || u.email}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 flex-wrap">
              <Mail className="h-3 w-3" />
              <span>{u.email}</span>
              <Badge variant="outline" className={`${STATUS_BADGE[u.status] || ""} capitalize text-[10px]`}>
                {u.status}
              </Badge>
              <Badge variant="outline" className={`${TIER_BADGE[u.tier] || ""} capitalize text-[10px]`}>
                {u.tier}
              </Badge>
              {u.email_verified && (
                <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px]">
                  Verified
                </Badge>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Profile */}
            <Section icon={UserIcon} title="Profile">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="ud-first">First name</Label>
                  <Input id="ud-first" value={first} onChange={(e) => setFirst(e.target.value)} data-testid="ud-first-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="ud-last">Last name</Label>
                  <Input id="ud-last" value={last} onChange={(e) => setLast(e.target.value)} data-testid="ud-last-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs" htmlFor="ud-phone">Phone</Label>
                  <Input id="ud-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+94 ..." data-testid="ud-phone-input" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs">Email (read-only)</Label>
                  <Input value={u.email} disabled />
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button onClick={saveProfile} disabled={busy.profile} className="gap-2" data-testid="ud-save-profile">
                  {busy.profile ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save profile
                </Button>
              </div>
            </Section>

            {/* Subscription Management */}
            <Section icon={Crown} title="Subscription Management">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tier</Label>
                  <div className="flex gap-2">
                    <Select value={tierDraft} onValueChange={setTierDraft}>
                      <SelectTrigger className="h-9 text-xs flex-1" data-testid="ud-tier-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basic">
                          <span className="inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Basic</span>
                        </SelectItem>
                        <SelectItem value="trial">
                          <span className="inline-flex items-center gap-2"><Hourglass className="h-3.5 w-3.5" /> Trial</span>
                        </SelectItem>
                        <SelectItem value="premium">
                          <span className="inline-flex items-center gap-2"><Crown className="h-3.5 w-3.5" /> Premium</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={saveTier}
                      disabled={busy.tier || tierDraft === u.tier}
                      variant="outline"
                      className="gap-2"
                      data-testid="ud-save-tier"
                    >
                      {busy.tier ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Apply
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Effective tier</Label>
                  <div className="h-9 rounded-md border bg-muted/40 px-3 flex items-center text-sm">
                    <Badge variant="outline" className={`${TIER_BADGE[u.effective_tier] || ""} capitalize text-[10px]`}>
                      {u.effective_tier}
                    </Badge>
                  </div>
                </div>
              </div>

              {(u.tier === "trial" || u.trial_started_at) && (() => {
                const expISO = u.trial_expires_at;
                const startISO = u.trial_started_at;
                const msLeft = expISO ? new Date(expISO).getTime() - Date.now() : null;
                const daysLeft = msLeft != null ? Math.ceil(msLeft / 86_400_000) : null;
                let daysCls = "text-foreground";
                let daysLabel = "—";
                if (daysLeft != null) {
                  if (daysLeft <= 0) {
                    daysCls = "text-[hsl(var(--destructive))]";
                    daysLabel = "Expired";
                  } else if (daysLeft <= 3) {
                    daysCls = "text-[hsl(var(--destructive))]";
                    daysLabel = `${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
                  } else if (daysLeft <= 7) {
                    daysCls = "text-[hsl(var(--warning))]";
                    daysLabel = `${daysLeft} days`;
                  } else {
                    daysCls = "text-[hsl(var(--success))]";
                    daysLabel = `${daysLeft} days`;
                  }
                }
                return (
                  <div className="rounded-md border bg-[hsl(var(--info))]/5 px-3 py-3 space-y-3" data-testid="trial-management">
                    <div className="flex items-center gap-2 text-xs font-medium">
                      <Hourglass className="h-3.5 w-3.5 text-[hsl(var(--info))]" />
                      Trial Management
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="rounded-md border bg-card px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Trial started</div>
                        <div className="text-sm font-medium" data-testid="trial-started">
                          {startISO ? formatDateTime(startISO) : "—"}
                        </div>
                      </div>
                      <div className="rounded-md border bg-card px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Trial expires</div>
                        <div className="text-sm font-medium" data-testid="trial-expires">
                          {expISO ? formatDateTime(expISO) : "—"}
                        </div>
                      </div>
                      <div className="rounded-md border bg-card px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Days remaining</div>
                        <div className={`text-sm font-semibold ${daysCls}`} data-testid="trial-days-remaining">
                          {daysLabel}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-md border bg-card px-3 py-2.5 space-y-2">
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Set expiry date</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={trialExpiryInput}
                          onChange={(e) => setTrialExpiryInput(e.target.value)}
                          className="h-9"
                          data-testid="trial-set-date-input"
                        />
                        <Button
                          onClick={setTrialDate}
                          disabled={busy["set-date"] || !trialExpiryInput}
                          size="sm"
                          variant="outline"
                          className="gap-2 whitespace-nowrap"
                          data-testid="trial-set-date-confirm"
                        >
                          {busy["set-date"] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          Set Date
                        </Button>
                      </div>
                    </div>

                    <div className="text-center text-[10px] uppercase tracking-wide text-muted-foreground">— or —</div>

                    <div className="rounded-md border bg-card px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs whitespace-nowrap">Extend by</Label>
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          value={extendDays}
                          onChange={(e) => setExtendDays(e.target.value)}
                          className="w-20 h-9"
                          data-testid="trial-extend-days"
                        />
                        <span className="text-xs text-muted-foreground">days</span>
                        <Button
                          onClick={extend}
                          disabled={busy.extend}
                          size="sm"
                          className="gap-2 ml-auto"
                          data-testid="trial-extend-confirm"
                        >
                          {busy.extend ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          Extend Trial
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="rounded-md border px-3 py-2.5 space-y-2">
                <div className="text-xs font-medium flex items-center gap-2">
                  <Crown className="h-3.5 w-3.5 text-[hsl(var(--warning))]" />
                  Grant Premium Access
                </div>
                <div className="flex items-end gap-2">
                  <div className="space-y-1.5 flex-1">
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Premium expires (optional — leave blank for indefinite)
                    </Label>
                    <Input
                      type="date"
                      value={premiumExpiresInput}
                      onChange={(e) => setPremiumExpiresInput(e.target.value)}
                      className="h-9"
                      data-testid="ud-premium-expires"
                    />
                  </div>
                  <Button
                    onClick={grantPremium}
                    disabled={busy.grant}
                    className="gap-2 bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-white"
                    data-testid="ud-grant-premium"
                  >
                    {busy.grant ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="h-3.5 w-3.5" />}
                    Grant Premium
                  </Button>
                </div>
                {u.premium_expires_at && (
                  <div className="text-[11px] text-muted-foreground">
                    Currently expires: {formatDateTime(u.premium_expires_at)}
                  </div>
                )}
              </div>
            </Section>

            {/* Account Actions */}
            <Section icon={ShieldCheck} title="Account Actions">
              <div className="flex flex-wrap gap-2">
                {u.status === "pending" && (
                  <>
                    <Button onClick={approve} disabled={busy.approve} className="gap-2" data-testid="ud-approve">
                      {busy.approve ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Approve Account
                    </Button>
                    <Button onClick={() => setReasonOpen("reject")} variant="outline" className="gap-2" data-testid="ud-reject">
                      <X className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" /> Reject
                    </Button>
                  </>
                )}
                {u.status === "approved" && (
                  <Button onClick={() => setReasonOpen("suspend")} variant="outline" className="gap-2 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30 hover:bg-[hsl(var(--destructive))]/10" data-testid="ud-suspend">
                    <AlertTriangle className="h-3.5 w-3.5" /> Suspend Account
                  </Button>
                )}
                {u.status === "rejected" && (
                  <Button onClick={reactivate} disabled={busy.reactivate} className="gap-2" data-testid="ud-reactivate">
                    {busy.reactivate ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Reactivate Account
                  </Button>
                )}
                {u.status_reason && (
                  <div className="w-full rounded-md border bg-muted/40 px-3 py-2 mt-1">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Last status reason</div>
                    <div className="text-xs">{u.status_reason}</div>
                  </div>
                )}
              </div>
            </Section>

            {/* Feature Access */}
            <Section icon={Globe2} title="Feature Access">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">CSE Stocks</div>
                    <div className="text-[11px] text-muted-foreground">Always enabled for every user.</div>
                  </div>
                  <Badge
                    variant="outline"
                    className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30"
                  >
                    Enabled
                  </Badge>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2">
                      US Stocks
                      {isAdmin && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">Admin · always on</Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Toggling immediately shows / hides US screener, portfolio,
                      watchlist, track record, alerts and Deep AI.
                    </div>
                  </div>
                  <Switch
                    data-testid="ud-us-stocks-toggle"
                    checked={usStocksEnabled}
                    disabled={isAdmin || !!busy.us_stocks}
                    onCheckedChange={toggleUsStocks}
                  />
                </div>
              </div>
            </Section>

            {/* Activity Log */}
            <Section icon={Activity} title="Recent activity (last 10)">
              {activityLoading ? (
                <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
              ) : activity.length === 0 ? (
                <div className="text-xs text-muted-foreground py-4 text-center">No recent activity recorded.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-1.5 font-medium">Action</th>
                      <th className="py-1.5 font-medium">Detail</th>
                      <th className="py-1.5 font-medium text-right">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((a, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5 capitalize whitespace-nowrap">{(a.action || "").replace(/_/g, " ")}</td>
                        <td className="py-1.5 text-muted-foreground truncate max-w-[200px]">{a.detail || "—"}</td>
                        <td className="py-1.5 text-right text-muted-foreground whitespace-nowrap">{formatDateTime(a.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="ud-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reasonOpen} onOpenChange={(o) => !o && setReasonOpen(null)}>
        <AlertDialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {reasonOpen === "suspend" ? "Suspend account" : "Reject registration"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {reasonOpen === "suspend"
                ? "Provide a reason — this is required and will be included in the email sent to the user."
                : "A reason is optional. If provided it will be included in the email sent to the user."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            rows={4}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={reasonOpen === "suspend" ? "e.g. Multiple terms-of-service violations" : "e.g. Could not verify identity"}
            data-testid="ud-reason-textarea"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitReason}
              disabled={busy.reason || (reasonOpen === "suspend" && !reasonText.trim())}
              className={reasonOpen === "suspend" ? "bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white" : ""}
              data-testid="ud-reason-confirm"
            >
              {busy.reason ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              {reasonOpen === "suspend" ? "Suspend account" : "Reject registration"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserDetailModal;
